import { useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

function DialogSurface({ title, description, children, actions, onCancel, initialFocusRef, returnFocusRef, size = 'standard', cancelDisabled = false, closeOnBackdrop = false }) {
  const id = useId();
  const dialogRef = useRef(null);
  const titleRef = useRef(null);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const previousOverflow = document.documentElement.style.overflow;
    dialog.showModal(); // Native top layer makes the rest of this document inert.
    document.documentElement.style.overflow = 'hidden';
    const initial = initialFocusRef?.current;
    if (initial && dialog.contains(initial) && !initial.matches(':disabled')) {
      initial.focus({ preventScroll: true });
    }
    if (!initial || document.activeElement !== initial) titleRef.current.focus({ preventScroll: true });

    return () => {
      if (dialog.open) dialog.close();
      document.documentElement.style.overflow = previousOverflow;
      const target = returnFocusRef?.current || opener;
      if (target?.isConnected && !target.matches(':disabled')) target.focus({ preventScroll: true });
    };
  }, [initialFocusRef, returnFocusRef]);

  const containTab = (event) => {
    if (event.key !== 'Tab') return;
    const nodes = [...dialogRef.current.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex], [contenteditable="true"]'
    )].filter(node => node.tabIndex >= 0 && !node.matches(':disabled')
      && !node.closest('[inert]') && node.getClientRects().length > 0
      && getComputedStyle(node).visibility !== 'hidden');
    const index = nodes.indexOf(document.activeElement);
    const atTitle = document.activeElement === titleRef.current;
    if (!nodes.length) {
      event.preventDefault();
      titleRef.current.focus();
    } else if (event.shiftKey && (index === 0 || atTitle)) {
      event.preventDefault();
      nodes[nodes.length - 1].focus();
    } else if (!event.shiftKey && (atTitle || index === nodes.length - 1)) {
      event.preventDefault();
      nodes[0].focus();
    }
  };

  return createPortal(
    // Portals do not inherit their former DOM ancestors' CSS variables/scope.
    <div data-fc-ui data-fc-dialog-portal>
      <dialog ref={dialogRef} className={`fc-dialog fc-dialog--${size}`} aria-modal="true"
        aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-description` : undefined}
        onClick={event => {
          if (!closeOnBackdrop || cancelDisabled || event.target !== event.currentTarget) return;
          const r = event.currentTarget.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) onCancel('backdrop');
        }}
        onKeyDown={containTab} onCancel={event => { event.preventDefault(); if (!cancelDisabled) onCancel('escape'); }}>
        <header className="fc-dialog__header">
          <h2 id={`${id}-title`} ref={titleRef} tabIndex={-1} className="fc-heading-2">{title}</h2>
          <Button variant="tertiary" aria-label={typeof title === 'string' ? `Close ${title}` : 'Close dialog'}
            disabled={cancelDisabled} onClick={() => onCancel('close')}>Close</Button>
          {description && <p id={`${id}-description`} className="fc-helper fc-dialog__description">{description}</p>}
        </header>
        <div className="fc-dialog__body" tabIndex={0}>{children}</div>
        {actions && <footer className="fc-dialog__footer">{actions}</footer>}
      </dialog>
    </div>, document.body
  );
}

/** Controlled modal. Parent closes by setting open=false; cancel never saves. */
export default function Dialog({ open, ...props }) {
  return open ? <DialogSurface {...props} /> : null;
}
